import { useCallback, useMemo, useState } from "react";
import { invoiceTemplateNameSchema } from "@common/schemas/domains/invoice/invoice-template.schema";
import { NativeInvoiceDraftBody } from "@common/schemas/domains/invoice/native-invoice.schema";
import { useCreateInvoiceTemplate } from "@api/invoiceTemplate.api";
import type { NativeInvoiceLineCodingByLineId } from "./nativeInvoiceFormTypes";
import { toInvoiceTemplateContentBody } from "./toInvoiceTemplateContentBody";
import { SaveResult, SaveStatus } from "./useNativeInvoiceSave";

interface Params {
    workspaceId: string | undefined;
    entityId: number | undefined;
    name: string;
    draft: NativeInvoiceDraftBody | null;
    draftLineCodingBaseline?: NativeInvoiceLineCodingByLineId;
    dirtyFingerprint: string;
    onSaveSuccess?: (meta: {
        savedLineCodingBaseline: NativeInvoiceLineCodingByLineId;
    }) => void;
}

export function useInvoiceTemplateCreateSave({
    workspaceId,
    entityId,
    name,
    draft,
    draftLineCodingBaseline,
    dirtyFingerprint,
    onSaveSuccess,
}: Params): SaveResult {
    const createMutation = useCreateInvoiceTemplate();
    const [status, setStatus] = useState<SaveStatus>("idle");
    const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);
    const [baselineFingerprint, setBaselineFingerprint] =
        useState(dirtyFingerprint);
    const [inFlight, setInFlight] = useState<Promise<
        string | undefined
    > | null>(null);

    const parsedName = useMemo(
        () => invoiceTemplateNameSchema.safeParse(name),
        [name],
    );
    const createAsync = createMutation.mutateAsync;

    const save = useCallback(async (): Promise<string | undefined> => {
        if (inFlight) {
            return await inFlight;
        }
        if (
            !workspaceId ||
            entityId === undefined ||
            !draft ||
            !parsedName.success
        ) {
            return undefined;
        }

        const savedLineCodingBaseline = draftLineCodingBaseline ?? {};
        const savedFingerprint = dirtyFingerprint;
        const request = createAsync({
            entityId,
            body: {
                name: parsedName.data,
                ...toInvoiceTemplateContentBody(draft),
            },
            suppressErrorToast: true,
        }).then((created) => {
            setLastSavedAt(new Date());
            setBaselineFingerprint(savedFingerprint);
            setStatus("saved");
            onSaveSuccess?.({ savedLineCodingBaseline });
            return created.id;
        });

        setStatus("saving");
        setInFlight(request);
        try {
            return await request;
        } catch (error) {
            setStatus("error");
            throw error;
        } finally {
            setInFlight(null);
        }
    }, [
        inFlight,
        workspaceId,
        entityId,
        draft,
        parsedName,
        draftLineCodingBaseline,
        dirtyFingerprint,
        createAsync,
        onSaveSuccess,
    ]);

    return {
        invoiceId: undefined,
        status,
        lastSavedAt,
        isDirty:
            baselineFingerprint !== dirtyFingerprint || name.trim().length > 0,
        acknowledgeEntityRescope: () => undefined,
        rebaseCleanBaselineAfterAddress: () => undefined,
        rebaseBaselineAfterTemplateApply: () => undefined,
        save,
    };
}
